import React, { useState, useEffect } from 'react';
import { FixedFeedbackWidget } from '@/components/feedback';
import { RewardInfo } from './reward-info';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  Trophy, 
  CheckCircle, 
  Clock, 
  Music, 
  Star,
  MessageSquare,
  ThumbsUp,
  ThumbsDown,
  ArrowRight,
  Heart
} from 'lucide-react';
import { toast } from 'sonner';
import { participantService } from '@/client/services/participants';

export interface SessionCompletionStats {
  sessionId: string;
  participantId: string;
  totalSongs: number;
  totalTime: number; // in milliseconds
  averageTimePerSong: number; // in milliseconds
  completedAt: string;
}

export interface FeedbackData {
  overallExperience: 'excellent' | 'good' | 'fair' | 'poor';
  platformUsability: 'very-easy' | 'easy' | 'neutral' | 'difficult' | 'very-difficult';
  audioQuality: 'excellent' | 'good' | 'fair' | 'poor';
  sessionLength: 'too-short' | 'just-right' | 'too-long';
  wouldRecommend: boolean;
  mostEnjoyedAspect?: string;
  suggestions?: string;
  additionalComments?: string;
}

interface SessionCompletionProps {
  stats: SessionCompletionStats;
  onComplete?: (feedback?: FeedbackData) => void;
  onSkipFeedback?: () => void;
  showFeedbackForm?: boolean;
  className?: string;
}

export function SessionCompletion({ 
  stats, 
  onComplete, 
  onSkipFeedback,
  showFeedbackForm = true,
  className 
}: SessionCompletionProps) {
  const [showFeedback, setShowFeedback] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [feedback, setFeedback] = useState<Partial<FeedbackData>>({});
  const [annotationCount, setAnnotationCount] = useState(0);
  const [isLoadingCount, setIsLoadingCount] = useState(true);

  // Fetch participant's annotation count
  useEffect(() => {
    const fetchAnnotationCount = async () => {
      try {
        const profile = await participantService.getMyProfile();
        setAnnotationCount(profile.participant.annotation_count || 0);
      } catch (error) {
        console.error('Failed to fetch annotation count:', error);
      } finally {
        setIsLoadingCount(false);
      }
    };

    fetchAnnotationCount();
  }, []);

  const formatTime = (milliseconds: number): string => {
    const minutes = Math.floor(milliseconds / 60000);
    const seconds = Math.floor((milliseconds % 60000) / 1000);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const formatDuration = (milliseconds: number): string => {
    const totalMinutes = Math.floor(milliseconds / 60000);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  };

  const handleSubmitFeedback = async () => {
    setIsSubmitting(true);
    try {
      // Validate required fields
      if (!feedback.overallExperience || !feedback.platformUsability || 
          !feedback.audioQuality || !feedback.sessionLength || 
          feedback.wouldRecommend === undefined) {
        toast.error('Please complete all required feedback fields');
        return;
      }

      // TODO: Submit feedback to API
      console.log('Submitting feedback:', feedback);
      
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      toast.success('Thank you for your feedback!');
      onComplete?.(feedback as FeedbackData);
    } catch (error) {
      console.error('Failed to submit feedback:', error);
      toast.error('Failed to submit feedback. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSkipFeedback = () => {
    onSkipFeedback?.();
    onComplete?.();
  };

  const getExperienceIcon = (experience: string) => {
    switch (experience) {
      case 'excellent':
        return <Star className="h-4 w-4 text-yellow-500" />;
      case 'good':
        return <ThumbsUp className="h-4 w-4 text-green-500" />;
      case 'fair':
        return <ThumbsUp className="h-4 w-4 text-yellow-500" />;
      case 'poor':
        return <ThumbsDown className="h-4 w-4 text-red-500" />;
      default:
        return null;
    }
  };

  if (showFeedback && showFeedbackForm) {
    return (
      <div className={className}>
        <div className="max-w-2xl mx-auto space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MessageSquare className="h-5 w-5 text-blue-600" />
                Help Us Improve
              </CardTitle>
              <p className="text-muted-foreground">
                Your feedback helps us improve the research platform for future participants.
              </p>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Overall Experience */}
              <div>
                <Label htmlFor="overallExperience">
                  How would you rate your overall experience? *
                </Label>
                <Select
                  value={feedback.overallExperience || ''}
                  onValueChange={(value: FeedbackData['overallExperience']) =>
                    setFeedback(prev => ({ ...prev, overallExperience: value }))
                  }
                >
                  <SelectTrigger className="mt-2">
                    <SelectValue placeholder="Select your rating" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="excellent">Excellent</SelectItem>
                    <SelectItem value="good">Good</SelectItem>
                    <SelectItem value="fair">Fair</SelectItem>
                    <SelectItem value="poor">Poor</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Platform Usability */}
              <div>
                <Label htmlFor="platformUsability">
                  How easy was the platform to use? *
                </Label>
                <Select
                  value={feedback.platformUsability || ''}
                  onValueChange={(value: FeedbackData['platformUsability']) =>
                    setFeedback(prev => ({ ...prev, platformUsability: value }))
                  }
                >
                  <SelectTrigger className="mt-2">
                    <SelectValue placeholder="Select usability rating" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="very-easy">Very Easy</SelectItem>
                    <SelectItem value="easy">Easy</SelectItem>
                    <SelectItem value="neutral">Neutral</SelectItem>
                    <SelectItem value="difficult">Difficult</SelectItem>
                    <SelectItem value="very-difficult">Very Difficult</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Audio Quality */}
              <div>
                <Label htmlFor="audioQuality">
                  How was the audio quality? *
                </Label>
                <Select
                  value={feedback.audioQuality || ''}
                  onValueChange={(value: FeedbackData['audioQuality']) =>
                    setFeedback(prev => ({ ...prev, audioQuality: value }))
                  }
                >
                  <SelectTrigger className="mt-2">
                    <SelectValue placeholder="Select audio quality rating" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="excellent">Excellent</SelectItem>
                    <SelectItem value="good">Good</SelectItem>
                    <SelectItem value="fair">Fair</SelectItem>
                    <SelectItem value="poor">Poor</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Session Length */}
              <div>
                <Label htmlFor="sessionLength">
                  How did you find the session length? *
                </Label>
                <Select
                  value={feedback.sessionLength || ''}
                  onValueChange={(value: FeedbackData['sessionLength']) =>
                    setFeedback(prev => ({ ...prev, sessionLength: value }))
                  }
                >
                  <SelectTrigger className="mt-2">
                    <SelectValue placeholder="Select session length feedback" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="too-short">Too Short</SelectItem>
                    <SelectItem value="just-right">Just Right</SelectItem>
                    <SelectItem value="too-long">Too Long</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Would Recommend */}
              <div>
                <Label htmlFor="wouldRecommend">
                  Would you recommend this research platform to others? *
                </Label>
                <Select
                  value={feedback.wouldRecommend?.toString() || ''}
                  onValueChange={(value) =>
                    setFeedback(prev => ({ ...prev, wouldRecommend: value === 'true' }))
                  }
                >
                  <SelectTrigger className="mt-2">
                    <SelectValue placeholder="Select recommendation" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="true">Yes</SelectItem>
                    <SelectItem value="false">No</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Most Enjoyed Aspect */}
              <div>
                <Label htmlFor="mostEnjoyedAspect">
                  What did you enjoy most about the experience?
                </Label>
                <Input
                  id="mostEnjoyedAspect"
                  value={feedback.mostEnjoyedAspect || ''}
                  onChange={(e) =>
                    setFeedback(prev => ({ ...prev, mostEnjoyedAspect: e.target.value }))
                  }
                  placeholder="e.g., Discovering new music, Easy interface, Interesting questions..."
                  className="mt-2"
                />
              </div>

              {/* Suggestions */}
              <div>
                <Label htmlFor="suggestions">
                  Any suggestions for improvement?
                </Label>
                <Textarea
                  id="suggestions"
                  value={feedback.suggestions || ''}
                  onChange={(e) =>
                    setFeedback(prev => ({ ...prev, suggestions: e.target.value }))
                  }
                  placeholder="Share your ideas for making the platform better..."
                  className="mt-2"
                  rows={3}
                />
              </div>

              {/* Additional Comments */}
              <div>
                <Label htmlFor="additionalComments">
                  Additional comments
                </Label>
                <Textarea
                  id="additionalComments"
                  value={feedback.additionalComments || ''}
                  onChange={(e) =>
                    setFeedback(prev => ({ ...prev, additionalComments: e.target.value }))
                  }
                  placeholder="Anything else you'd like to share..."
                  className="mt-2"
                  rows={3}
                />
              </div>

              <div className="flex justify-between pt-4">
                <Button variant="outline" onClick={handleSkipFeedback}>
                  Skip Feedback
                </Button>
                <Button onClick={handleSubmitFeedback} disabled={isSubmitting} className="gap-2">
                  {isSubmitting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Submitting...
                    </>
                  ) : (
                    <>
                      Submit Feedback
                      <ArrowRight className="h-4 w-4" />
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
        <FixedFeedbackWidget context="completion" />
      </div>
    );
  }

  return (
    <div className={className}>
      <div className="max-w-2xl mx-auto space-y-6">
        {/* Reward Status */}
        {!isLoadingCount && (
          <RewardInfo variant="completion" annotationCount={annotationCount} />
        )}

        {/* Completion Celebration */}
        <Card>
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-green-100 dark:bg-green-900">
              <Trophy className="h-8 w-8 text-green-600 dark:text-green-400" />
            </div>
            <CardTitle className="text-2xl">Session Complete!</CardTitle>
            <p className="text-muted-foreground">
              Thank you for participating in our music annotation research
            </p>
          </CardHeader>
          <CardContent>
            <div className="text-center space-y-4">
              <div className="flex items-center justify-center gap-2">
                <CheckCircle className="h-5 w-5 text-green-600" />
                <span className="font-medium">Your responses have been recorded</span>
              </div>
              <p className="text-sm text-muted-foreground">
                Your annotations will contribute to important research on music perception and AI-generated content.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Session Statistics */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Music className="h-5 w-5" />
              Your Session Summary
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center p-4 bg-muted/50 rounded-lg">
                <div className="text-2xl font-bold text-primary">{stats.totalSongs}</div>
                <div className="text-sm text-muted-foreground">Songs Annotated</div>
              </div>
              <div className="text-center p-4 bg-muted/50 rounded-lg">
                <div className="text-2xl font-bold">{formatDuration(stats.totalTime)}</div>
                <div className="text-sm text-muted-foreground">Total Time</div>
              </div>
              <div className="text-center p-4 bg-muted/50 rounded-lg">
                <div className="text-2xl font-bold">{formatTime(stats.averageTimePerSong)}</div>
                <div className="text-sm text-muted-foreground">Avg per Song</div>
              </div>
              <div className="text-center p-4 bg-muted/50 rounded-lg">
                <div className="flex items-center justify-center">
                  <Heart className="h-6 w-6 text-red-500" />
                </div>
                <div className="text-sm text-muted-foreground">Thank You!</div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Research Impact */}
        <Card>
          <CardHeader>
            <CardTitle>Research Impact</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-muted-foreground">
              Your participation helps researchers understand:
            </p>
            <ul className="space-y-2 text-sm">
              <li className="flex items-center gap-2">
                <CheckCircle className="h-4 w-4 text-green-600" />
                How people distinguish between AI-generated and human-composed music
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="h-4 w-4 text-green-600" />
                The relationship between musical background and perception
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="h-4 w-4 text-green-600" />
                Factors that influence aesthetic and emotional responses to music
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="h-4 w-4 text-green-600" />
                How to improve AI music generation systems
              </li>
            </ul>
          </CardContent>
        </Card>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          {showFeedbackForm && (
            <Button onClick={() => setShowFeedback(true)} className="gap-2">
              <MessageSquare className="h-4 w-4" />
              Provide Feedback
            </Button>
          )}
          <Button variant="outline" onClick={() => onComplete?.()} className="gap-2">
            <ArrowRight className="h-4 w-4" />
            Continue
          </Button>
        </div>

        {/* Session Details */}
        <div className="text-center text-xs text-muted-foreground">
          <p>Session ID: {stats.sessionId}</p>
          <p>Completed: {new Date(stats.completedAt).toLocaleString()}</p>
        </div>
      </div>
      <FixedFeedbackWidget context="completion" />
    </div>
  );
}